/**
 * Shared spotlight tour.
 *
 * Element-backed steps are optional by default: if a page does not render a
 * target (empty state, permissions, responsive layout, etc.) the tour skips
 * that step instead of leaving an inert tooltip on screen.
 */

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { X, ChevronRight, HelpCircle } from 'lucide-react';

const TOOLTIP_WIDTH = 320;
const TOOLTIP_HEIGHT = 200;
const GAP = 20;
const PAD = 8;

export function resolveTourElement(selector) {
  if (!selector) return null;
  try {
    return document.querySelector(selector);
  } catch {
    return null;
  }
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function getPlacement(rect, position) {
  let top;
  let left;

  switch (position) {
    case 'top':
      top = rect.top - TOOLTIP_HEIGHT - GAP;
      left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - TOOLTIP_HEIGHT / 2;
      left = rect.left - TOOLTIP_WIDTH - GAP;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - TOOLTIP_HEIGHT / 2;
      left = rect.right + GAP;
      break;
    case 'bottom':
    default:
      top = rect.bottom + GAP;
      left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
      break;
  }

  return {
    top: `${clamp(top, 20, window.innerHeight - TOOLTIP_HEIGHT - 20)}px`,
    left: `${clamp(left, 20, window.innerWidth - TOOLTIP_WIDTH - 20)}px`,
    transform: 'none',
  };
}

const CENTER_POSITION = {
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
};

const TourGuide = ({ steps = [], storageKey = 'tour-completed', onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [ready, setReady] = useState(false);
  const [tooltipPos, setTooltipPos] = useState(CENTER_POSITION);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const placementFrameRef = useRef(null);
  const measureFrameRef = useRef(null);
  const transitionTokenRef = useRef(0);
  const clickLockRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  const onSkipRef = useRef(onSkip);
  const maskId = `garage-tour-mask-${useId().replace(/:/g, '')}`;

  onCompleteRef.current = onComplete;
  onSkipRef.current = onSkip;

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  const finish = useCallback((skipped = false) => {
    clickLockRef.current = false;
    setIsVisible(false);
    localStorage.setItem(storageKey, 'true');
    if (skipped) onSkipRef.current?.();
    else onCompleteRef.current?.();
  }, [storageKey]);

  const measureTarget = useCallback((element, stepDef) => {
    if (!element?.isConnected) return false;
    const rect = element.getBoundingClientRect();
    setSpotlightRect({
      top: rect.top - PAD,
      left: rect.left - PAD,
      width: rect.width + PAD * 2,
      height: rect.height + PAD * 2,
    });
    setTooltipPos(getPlacement(rect, stepDef.position));
    return true;
  }, []);

  useEffect(() => {
    transitionTokenRef.current += 1;
    const token = transitionTokenRef.current;
    setReady(false);

    if (!step) return undefined;

    if (step.position === 'center' || !step.element) {
      setSpotlightRect(null);
      setTooltipPos(CENTER_POSITION);
      setReady(true);
      clickLockRef.current = false;
      return undefined;
    }

    // Let React finish the page paint, then resolve the target once. Missing
    // data/permission-dependent targets are skipped immediately by default.
    placementFrameRef.current = requestAnimationFrame(() => {
      if (transitionTokenRef.current !== token) return;
      const element = resolveTourElement(step.element);

      if (!element) {
        if (step.optional !== false) {
          if (isLastStep) finish(false);
          else setCurrentStep((value) => value + 1);
          return;
        }
        setSpotlightRect(null);
        setTooltipPos(CENTER_POSITION);
        setReady(true);
        clickLockRef.current = false;
        return;
      }

      // Instant scrolling avoids competing with scroll-driven measurements and
      // removes the old fixed pause before every highlighted step.
      element.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
      placementFrameRef.current = requestAnimationFrame(() => {
        if (transitionTokenRef.current !== token) return;
        measureTarget(element, step);
        setReady(true);
        clickLockRef.current = false;
      });
    });

    const scheduleMeasure = () => {
      if (measureFrameRef.current) cancelAnimationFrame(measureFrameRef.current);
      measureFrameRef.current = requestAnimationFrame(() => {
        if (transitionTokenRef.current !== token) return;
        const element = resolveTourElement(step.element);
        if (element) measureTarget(element, step);
      });
    };

    window.addEventListener('scroll', scheduleMeasure, true);
    window.addEventListener('resize', scheduleMeasure);

    return () => {
      transitionTokenRef.current += 1;
      window.removeEventListener('scroll', scheduleMeasure, true);
      window.removeEventListener('resize', scheduleMeasure);
      if (placementFrameRef.current) cancelAnimationFrame(placementFrameRef.current);
      if (measureFrameRef.current) cancelAnimationFrame(measureFrameRef.current);
    };
  }, [finish, isLastStep, measureTarget, step]);

  const handleNext = () => {
    if (!ready || clickLockRef.current) return;
    clickLockRef.current = true;
    if (isLastStep) finish(false);
    else {
      setReady(false);
      setCurrentStep((value) => value + 1);
    }
  };

  if (!isVisible || !steps.length || !step) return null;

  return (
    <>
      <svg
        className="garage-tour-overlay"
        aria-hidden="true"
        style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'auto', zIndex: 9998 }}
      >
        <defs>
          <mask id={maskId}>
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {spotlightRect && (
              <rect x={spotlightRect.left} y={spotlightRect.top} width={spotlightRect.width} height={spotlightRect.height} rx="12" fill="black" />
            )}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(0, 0, 0, 0.7)" mask={`url(#${maskId})`} />
      </svg>

      {spotlightRect && (
        <div
          className="garage-tour-spotlight-border"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: `${spotlightRect.width}px`,
            height: `${spotlightRect.height}px`,
            transform: `translate3d(${spotlightRect.left}px, ${spotlightRect.top}px, 0)`,
            pointerEvents: 'none',
            zIndex: 9999,
          }}
        />
      )}

      <div
        className={`garage-tour-tooltip ${ready ? 'is-ready' : 'is-placing'}`}
        role="dialog"
        aria-modal="true"
        aria-busy={!ready}
        aria-labelledby={`${maskId}-title`}
        style={{ top: tooltipPos.top, left: tooltipPos.left, transform: tooltipPos.transform, position: 'fixed', zIndex: 10000 }}
      >
        <button className="garage-tour-close" onClick={() => finish(true)} title="Close tour" aria-label="Close tour" type="button">
          <X size={16} />
        </button>

        <div className="garage-tour-header">
          <HelpCircle size={20} className="garage-tour-icon" />
          <h3 id={`${maskId}-title`}>{step.title}</h3>
        </div>

        <p className="garage-tour-description">{step.description}</p>

        <div className="garage-tour-footer">
          <div className="garage-tour-progress">Step {currentStep + 1} of {steps.length}</div>
          <div className="garage-tour-actions">
            <button type="button" className="garage-btn garage-btn-secondary garage-btn-sm" onClick={() => finish(true)}>
              Skip Tour
            </button>
            <button type="button" className="garage-btn garage-btn-primary garage-btn-sm" onClick={handleNext} disabled={!ready}>
              {isLastStep ? 'Got it!' : 'Next'}
              {!isLastStep && <ChevronRight size={14} />}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TourGuide;
